// @flow
import React from 'react'
import I18n from 'i18n-react'
import Immutable from 'seamless-immutable'
import { FieldForm, Popover } from '../../Components/'
import type { DefaultMiniAppProps } from './'
import type { InsuranceCompaniesType } from '../../Core/Types/InsuranceCompanyTypes'
import type { FormValueType } from '../../Core/Types/Values'
import { StringEnum } from '../_formFields'

type BonusMalusAppProps = DefaultMiniAppProps & {
  miniAppData: {
    insuranceCompanies: InsuranceCompaniesType,
  },
}

type LastBonusesType = {
  [identificationNumber: string]: {
    [insuranceCompany: string]: {
      monthsWithAccidents: number,
      countOfAccidents: number,
    },
  },
}

type BonusMalusAppState = {
  setDataCl: null | Function,
  setInputHelps: null | Function,
  setValidationErrors: null | Function,
  lastBonuses: LastBonusesType,
}

const POLICE_HOLDER_TYPE = {
  in: {
    '0': 'person',
    '1': 'company',
    '2': 'self-employed',
  },
  out: {
    person: 0,
    company: 1,
    'self-employed': 2,
  },
}

const VEHICLE_TYPE = {
  in: {
    car: 'car',
    moto: 'motorcycle',
    utility: 'van',
    truck: 'truck',
    trailer: 'trailer',
    tractor: 'tractor',
    caravan: 'campervan',
    work: 'work-machine',
    freighter: 'rig',
    bus: 'bus',
    ambulance: 'ambulance',
  },
  out: {
    car: 'car',
    motorcycle: 'moto',
    van: 'utility',
    truck: 'truck',
    trailer: 'trailer',
    tractor: 'tractor',
    campervan: 'caravan',
    'work-machine': 'work',
    rig: 'freighter',
    bus: 'bus',
    ambulance: 'ambulance',
  },
}

const MANUAL_SPECIFICATION = {
  name: 'bm-manually',
  fields: [
    {
      name: 'monthsWithAccidents',
      domain: {
        _type: 'int',
        min: 0,
        max: null,
        step: 1,
      },
      enabled: true,
      required: true,
    },
    {
      name: 'countOfAccidents',
      domain: {
        _type: 'int',
        min: 0,
        max: null,
        step: 1,
      },
      enabled: true,
      required: true,
    },
  ],
  types: [],
  dependencies: {},
}

const MANUAL_CONTENT = {
  types: {
    party: {
      fieldNames: {
        companyID: 'IČO',
        birthNumber: 'Rodné číslo',
      },
      fieldOrder: ['companyID', 'birthNumber'],
      valueNames: {},
      components: {
        companyID: {
          inputType: 'customCompanyID',
        },
      },
    },
    policyHolderParty: {
      fieldNames: {
        firstName: 'Vaše jméno',
        lastName: 'Vaše příjmení',
        companyID: 'IČO',
        companyName: 'Obchodní jméno',
        birthNumber: 'Rodné číslo',
      },
      fieldOrder: ['companyID', 'firstName', 'lastName', 'companyName', 'birthNumber'],
      valueNames: {
        type: {
          person: 'Fyzická osoba',
          'self-employed': 'Podnikatel (OSVČ)',
          company: 'Právnická osoba',
        },
      },
      components: {
        companyID: {
          inputType: 'customCompanyID',
        },
      },
    },
  },
  fieldNames: {},
  fieldOrder: ['policyHolder', 'user'],
  valueNames: {},
  components: {},
}

export default class BonusMalusApp extends React.Component<BonusMalusAppProps, BonusMalusAppState> {
  static defaultProps = {
    isBackOffice: false,
  }

  state = {
    setDataCl: null,
    setInputHelps: null,
    setValidationErrors: null,
    lastBonuses: {},
  }

  componentDidUpdate = (prevProps: BonusMalusAppProps) => {
    const { inputHelps, formData, isBackOffice, validationErrors, pushIsPristine } = this.props
    const formDataFields = ['policyHolder', 'type', 'bonusType', 'countOfAccidents', 'monthsWithAccidents']
    if (prevProps.inputHelps !== inputHelps && this.state.setInputHelps) {
      this.state.setInputHelps(inputHelps)
    }
    if (
      formDataFields.reduce(
        (acc, field) => acc || JSON.stringify(prevProps.formData[field]) !== JSON.stringify(formData[field]),
        false,
      ) ||
      this.areDiscountsDifferent(prevProps)
    ) {
      if (this.state.setDataCl !== null && isBackOffice) {
        this.handleSetData(this.props, this.state.setDataCl, false)
      }
      if (
        formData.bonusType === 'manually' &&
        (prevProps.formData.countOfAccidents !== formData.countOfAccidents ||
          prevProps.formData.monthsWithAccidents !== formData.monthsWithAccidents)
      ) {
        this.parseFinalData({
          bonusType: 'manually',
        })
      }
    }
    if (
      prevProps.validationErrors &&
      validationErrors &&
      prevProps.validationErrors.bonusType !== validationErrors.bonusType
    ) {
      if (this.state.setValidationErrors) {
        this.state.setValidationErrors({ bonusType: !pushIsPristine ? validationErrors.bonusType : undefined })
      }
    }
  }

  getDiscountsFromCalculatorNameObject = () => {
    const { miniAppData } = this.props
    if (miniAppData.discounts && miniAppData.discounts.asMutable) {
      let { discounts } = miniAppData
      let bonusMalusPartner = null
      let selectedCalculatorName = null
      Object.keys(discounts).forEach((calculatorName) => {
        if (discounts.getIn([calculatorName, 'bonusMalusPartner', 'type'])) {
          bonusMalusPartner = discounts.getIn([calculatorName, 'bonusMalusPartner'])
          selectedCalculatorName = calculatorName
        }
      })
      return [bonusMalusPartner, selectedCalculatorName]
    }
    return [null, null]
  }

  areDiscountsDifferent = (prevProps: BonusMalusAppProps) => {
    const { miniAppData } = this.props
    const discountFields = ['birthNumber', 'companyID', 'companyName', 'firstName', 'lastName', 'type']
    if (
      miniAppData.discounts &&
      miniAppData.discounts.asMutable &&
      prevProps.miniAppData.discounts &&
      prevProps.miniAppData.discounts.asMutable
    ) {
      const [bonusMalusPartner, selectedCalculatorName] = this.getDiscountsFromCalculatorNameObject()
      const prevBonusMalusParter = prevProps.miniAppData.discounts.getIn([selectedCalculatorName, 'bonusMalusPartner'])
      return [
        bonusMalusPartner &&
          prevBonusMalusParter &&
          !discountFields.every(
            (fieldKey) => bonusMalusPartner.getIn([fieldKey]) === prevBonusMalusParter.getIn([fieldKey]),
          ),
        bonusMalusPartner,
      ]
    }
    return [false, null]
  }

  getDataFromBMData = () => {
    const { formData } = this.props
    const bonusType = formData.getIn(['bonuses', 'userDataObject', 'bonusType'], false)
    const userData = formData.getIn(['bonuses', 'userDataObject', 'userData'], false)
    const partnerData = formData.getIn(['bonuses', 'userDataObject', 'partnerData'], false)
    const rawBonuses = formData.getIn(['bonuses', 'userDataObject', 'rawBonuses'], [])

    let data: { [string]: string } = {
      bonuses: rawBonuses,
      bonusType,
      vehicleType: VEHICLE_TYPE.out[formData.getIn(['bonuses', 'userDataObject', 'vehicleType'], 'car')],
      policyHolderType: POLICE_HOLDER_TYPE.out[userData.type],
      identificationNumber: userData.type !== 'person' ? userData.companyID : '',
      businessName: userData.companyName,
      name: userData.firstName,
      personalIdentificationNumber: userData.type !== 'company' ? userData.birthNumber : '',
    }

    if (partnerData && Object.keys(partnerData).length > 0) {
      data.mergeType = POLICE_HOLDER_TYPE.out[partnerData.type]
      data.mergeIdentificationNumber = partnerData.companyID
      data.mergeBussinesName = partnerData.companyName
      data.mergeName = partnerData.firstName
      data.mergeLastName = partnerData.lastName
      data.mergePersonalIdentificationNumber = partnerData.birthNumber
    }

    return data
  }

  getDataFromProps = (init?: boolean) => {
    const { formData, isBackOffice } = this.props
    const policyHolderType = formData.getIn(['policyHolder', 'type'], 'person')
    const [bonusMalusPartner, selectedCalculatorName] = this.getDiscountsFromCalculatorNameObject()
    let data: { [string]: string } = {
      policyHolderType: POLICE_HOLDER_TYPE.out[policyHolderType],
      name: formData.getIn(['policyHolder', 'firstName'], ''),
      identificationNumber: policyHolderType !== 'person' ? formData.getIn(['policyHolder', 'companyID'], '') : '',
      personalIdentificationNumber:
        policyHolderType !== 'company' ? formData.getIn(['policyHolder', 'birthNumber'], '').trim() : '',
      businessName: formData.getIn(['policyHolder', 'companyName'], ''),
      vehicleType: VEHICLE_TYPE.out[formData.getIn(['type'], 'car')],
    }
    if (init) {
      data.bonusType = formData.getIn(['bonusType'], isBackOffice ? 'ckp' : '')
    }
    if (
      selectedCalculatorName &&
      ['companyID', 'companyName', 'firstName', 'lastName', 'birthNumber'].some((key) => !!bonusMalusPartner[key])
    ) {
      if (bonusMalusPartner.companyID || bonusMalusPartner.birthNumber) {
        data.bonusType = 'combined'
      }
      data.mergeType = POLICE_HOLDER_TYPE.out[bonusMalusPartner.type]
      data.mergeIdentificationNumber = bonusMalusPartner.companyID
      data.mergeBussinesName = bonusMalusPartner.companyName
      data.mergeName = bonusMalusPartner.firstName
      data.mergeLastName = bonusMalusPartner.lastName
      data.mergePersonalIdentificationNumber = bonusMalusPartner.birthNumber
    }
    return data
  }

  handleCreateDataObject = (data: Object) => {
    const { formData } = this.props

    if (data.bonusType === 'manually') {
      return {}
    }

    const userData = {
      type: POLICE_HOLDER_TYPE.in[data.policyHolderType],
      companyID: data.identificationNumber,
      companyName: data.businessName,
      firstName: data.name,
      lastName: formData.getIn(['policyHolder', 'lastName'], 'undefined'),
      birthNumber: data.personalIdentificationNumber,
    }

    let partnerData = {}
    if (data.bonusType === 'combined') {
      partnerData = {
        type: POLICE_HOLDER_TYPE.in[data.mergeType],
        companyID: data.mergeIdentificationNumber,
        companyName: data.mergeBussinesName,
        firstName: data.mergeName,
        lastName: data.mergeLastName,
        birthNumber: data.mergePersonalIdentificationNumber,
      }
    }

    return {
      rawBonuses: data.bonuses,
      bonusType: data.bonusType,
      vehicleType: data.vehicleType,
      userData,
      partnerData,
    }
  }

  handleSetDiscounts = (bonusesPush?: Object) => {
    const { miniAppData, formData } = this.props
    if (miniAppData.enabledCalculators && typeof miniAppData.setDiscounts === 'function') {
      const bonuses = typeof bonusesPush === 'object' ? bonusesPush : {}
      const bonusesFromData = formData.getIn(['bonuses'], {})
      if (bonusesFromData) {
        // TODO check if identificationNumber is the same
      }
      let finalDiscounts = miniAppData.enabledCalculators.reduce((discountsAcc, discountCalculator) => {
        if (discountCalculator) {
          discountsAcc = discountsAcc.setIn([discountCalculator, 'bonusMalus'], bonuses[discountCalculator])
        }
        return discountsAcc
      }, this.props.miniAppData.discounts)
      this.props.miniAppData.setDiscounts(finalDiscounts)
    }
  }

  parseFinalData = (data: Object) => {
    const { miniAppData, formData, isBackOffice } = this.props

    let bonuses = {}
    if (!data.bonuses) {
      data.bonuses = []
    }
    let approximatedBonuses = [...data.bonuses]
      .filter((bonus) => !!bonus.since2000 && bonus.since2000.months !== 0)
      .sort((bonusA, bonusB) => bonusA.since2000.months - bonusB.since2000.months)
    const bonusesByKeys = data.bonuses.reduce((acc, bonus) => {
      if (bonus.since2000) {
        acc[bonus.name] = {
          monthsWithAccidents: bonus.since2000.months || 0,
          countOfAccidents: bonus.since2000.claims || 0,
          entryType: 'checked',
        }
      }
      return acc
    }, {})
    if (['manually', 'notSure'].includes(data.bonusType) || !isBackOffice) {
      approximatedBonuses = {
        entryType: 'manual',
        monthsWithAccidents: formData.getIn(['monthsWithAccidents'], 0)
          ? formData.getIn(['monthsWithAccidents'], 0)
          : 0,
        countOfAccidents: formData.getIn(['countOfAccidents'], 0) ? formData.getIn(['countOfAccidents'], 0) : 0,
      }
    } else if (approximatedBonuses.length === 0) {
      approximatedBonuses = {
        entryType: 'checked-approximated',
        monthsWithAccidents: 0,
        countOfAccidents: 0,
      }
    } else {
      approximatedBonuses = {
        entryType: 'checked-approximated',
        monthsWithAccidents: 0, // approximatedBonuses[0].since2000.months,
        countOfAccidents: 0, // approximatedBonuses[0].since2000.claims,
      }
    }

    if (typeof miniAppData.insuranceCompanies === 'object') {
      bonuses = Object.keys(miniAppData.insuranceCompanies).reduce((acc, insuranceCompanyKey) => {
        if (
          Array.isArray(miniAppData.insuranceCompanies[insuranceCompanyKey].calculators) &&
          miniAppData.insuranceCompanies[insuranceCompanyKey].calculators.length
        ) {
          miniAppData.insuranceCompanies[insuranceCompanyKey].calculators.forEach((calculatorName) => {
            if (calculatorName.toLowerCase().includes('car')) {
              if (bonusesByKeys[insuranceCompanyKey]) {
                acc[calculatorName] = bonusesByKeys[insuranceCompanyKey]
              } else {
                // $FlowIssue
                acc[calculatorName] = { ...approximatedBonuses }
              }
            }
          })
        }
        return acc
      }, {})
    }

    const userDataObject = this.handleCreateDataObject(data)
    this.handleSetDiscounts(bonuses, userDataObject)

    return {
      userDataObject,
      bonuses,
      approximatedBonuses,
    }
  }

  handleSetData = (props: BonusMalusAppProps, setDataCl: Function, init?: boolean) => {
    let data = {}

    const BMData = this.getDataFromBMData()
    const propsData = this.getDataFromProps(init)

    const checkFields = [
      'vehicleType',
      'policyHolderType',
      'identificationNumber',
      'businessName',
      'name',
      'personalIdentificationNumber',
      'bonusType',
      'mergeType',
      'mergeIdentificationNumber',
      'mergeBussinesName',
      'mergeName',
      'mergeLastName',
      'mergePersonalIdentificationNumber',
    ]
    const isTheSame = checkFields.reduce((acc, fieldName) => acc && BMData[fieldName] === propsData[fieldName], true)
    if (isTheSame) {
      data = BMData
    } else {
      data = propsData
    }
    setDataCl(data)
    return data
  }

  handleOnCreate = (setDataCl: Function, setInputHelps: Function, setValidationErrors: Function) => {
    this.setState({ setDataCl, setInputHelps, setValidationErrors })
    if (this.props.inputHelps) {
      setInputHelps(this.props.inputHelps)
    }
    const data = this.handleSetData(this.props, setDataCl, true)
    this.handleFinish(data)
  }

  handleFinish = (data: Object) => {
    const { onChangeFormData, formData } = this.props
    let newFormData = new Immutable({})
    let monthsWithAccidents = formData.getIn(['monthsWithAccidents'], 0)
    if (!monthsWithAccidents) {
      // can be ''
      monthsWithAccidents = 0
    }
    let countOfAccidents = 0
    if (monthsWithAccidents === 0) {
      monthsWithAccidents = 150
    }
    if (
      data.bonusType !== formData.getIn(['bonusType']) ||
      monthsWithAccidents !== formData.getIn(['monthsWithAccidents']) ||
      countOfAccidents !== formData.getIn(['countOfAccidents'])
    ) {
      onChangeFormData(
        newFormData
          .set('bonusType', data.bonusType || '')
          .set('monthsWithAccidents', monthsWithAccidents)
          .set('countOfAccidents', countOfAccidents),
        ['monthsWithAccidents', 'countOfAccidents'],
      )
    }
  }

  handleRenderHelp = (fieldName: string) => {
    if (this.props.inputHelps && this.props.inputHelps[fieldName]) {
      return (
        <Popover
          content={this.props.inputHelps[fieldName].content}
          id={`bonusMalusApp-${fieldName}`}
          title={this.props.inputHelps[fieldName].title}
        />
      )
    }
    return null
  }

  handleOnChangeValue = (key: string, value: FormValueType, pathArray: Array<string | number>) => {
    this.props.onChangeValue(key, value, pathArray)
  }

  onChangeBonusType = (bonusType: string) => {
    this.handleFinish({
      bonusType,
    })
  }

  render() {
    const {
      isBackOffice,
      formData,
      inputHelps,
      pushIsPristine,
      fullContent,
      fullSpecification,
      activeSettings,
    } = this.props

    let content = {
      domain: { values: ['notSure', 'manually'] },
      valueNames: {
        notSure: I18n.translate('bonusMalus.general.bonusType.notSure'),
        manually: I18n.translate('bonusMalus.general.bonusType.know'),
      },
    }

    return [
      !isBackOffice && (
        <div className="text-center" key="bonusMalus-title">
          <h2>{I18n.translate('miniApps.bonusMalus.title')}</h2>
        </div>
      ),
      <div id="empty_bonus" key="empty_bonus" />,
      <hr className="border-0 border-sm-top border-active-object" key="bonusMalus-hr-top" />,
      <div
        className="form-group d-flex flex-row flex-wrap flex-sm-row align-items-center"
        key="bonusMalus-bonusType-select"
      >
        <div className="col-10 col-sm-4 order-0 order-sm-0 text-sm-right">
          <label className="mb-0 mr-4 font-weight-bold" htmlFor="bonusType">
            <span className="text-nowrap">{I18n.translate('bonusMalus.general.bonusTypeLabelPart1')}</span>
            <br />
            <span className="text-nowrap">{I18n.translate('bonusMalus.general.bonusTypeLabelPart2')}</span>
            &nbsp;<span className="text-danger">*</span>
          </label>
        </div>
        <div className="col-12 col-sm-5 order-3 order-sm-0 my-2 pr-md-0">
          <StringEnum
            domain={content.domain}
            enabled
            id="bonusType"
            name="bonusType"
            onChange={this.onChangeBonusType}
            validationErrors={this.props.validationErrors}
            value={formData.bonusType}
            valueNames={content.valueNames}
          />
        </div>
        {inputHelps && !!inputHelps.bonusType && (
          <div className="col-2 col-sm-3 order-0 order-sm-0 text-left">
            {this.handleRenderHelp(formData.bonusType === 'bonusType')}
          </div>
        )}
      </div>,
      formData.bonusType === 'fromCKP' && (
        <div className="phone-app-ab-test-wrapper pb-2 mb-4" key="PhoneApp-content">
          <div className="form-group d-flex flex-row flex-wrap">
            <div className="ab-test-item col-10 col-sm-4 text-sm-center d-none d-sm-block mt-3">
              <img
                className="phone-app-ab-image"
                src={`${activeSettings.baseImagePath.replace('all', 'sro')}/robot-magnifier.png`}
              />
            </div>
            <div className="ab-test-item col-12 col-sm-5 mt-2 mt-sm-0 pr-sm-0">
              <div className="d-flex">
                <p className="text-center">
                  Abychom mohli u ČKP ověřit Váš bonus, prosíme Vás o vyplnění Vašeho křestního jména a rodného čísla
                  (pokud podnikáte, pak Vašeho IČ a názvu společnosti)
                  <br />
                  <br />V souladu s legislativou na ochranu osobních údajů nebudou tyto údaje nikde ukládány ani
                  využívány k jiným účelům.
                </p>
                {inputHelps && !!inputHelps.bonusType && (
                  <div className="d-sm-none pt-2">{this.handleRenderHelp('fromCKP')}</div>
                )}
              </div>
            </div>
            {inputHelps && !!inputHelps.bonusType && (
              <div className="ab-test-item col-2 col-sm-3 d-none d-sm-block">{this.handleRenderHelp('fromCKP')}</div>
            )}
          </div>
          <FieldForm
            blockClassName=""
            blockClassNameForR=""
            blockContentClassName=""
            content={MANUAL_CONTENT}
            data={formData}
            formData={formData}
            inputHelps={inputHelps}
            key="bonusMalusForm"
            onChangeValue={this.handleOnChangeValue}
            pushIsPristine={pushIsPristine}
            showBlockTitle={false}
            specification={fullSpecification}
            validationErrors={{}}
          />
        </div>
      ),
      formData.bonusType === 'manually' && (
        <FieldForm
          blockClassName=""
          blockClassNameForR=""
          blockContentClassName=""
          content={fullContent}
          data={formData}
          formData={formData}
          inputHelps={inputHelps}
          key="bonusMalusForm"
          onChangeValue={this.handleOnChangeValue}
          pushIsPristine={pushIsPristine}
          showBlockTitle={false}
          specification={MANUAL_SPECIFICATION}
          validationErrors={{}}
        />
      ),
    ]
  }
}
